<template>
  <section class="flight-selector" :aria-label="copy.title">
    <div class="flight-selector__header">
      <div class="flight-selector__intro">
        <span class="flight-selector__badge" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M19.6 4.4a1.6 1.6 0 0 0-2.3 0l-3.4 3.6-8.3-2.8-1.8 1.8 7 4.3-3.5 3.6-2.2-.4-1.6 1.6 3.3 1.2 1.2 3.3 1.6-1.6-.4-2.2 3.6-3.5 4.3 7 1.8-1.8-2.8-8.3 3.6-3.4c.6-.6.7-1.7.1-2.3Z" />
          </svg>
        </span>
        <div>
          <h3 class="flight-selector__title">{{ copy.title }}</h3>
          <p class="flight-selector__description">{{ copy.description }}</p>
        </div>
      </div>
      <button type="button" class="flight-selector__text-action" @click="$emit('manual')">
        {{ $t('home.flight.flight_search.enter_manually') }}
      </button>
    </div>

    <template v-if="showSelectedSummary">
      <div class="flight-selector__selected">
        <div class="flight-selector__selected-label">
          <span class="flight-selector__check" aria-hidden="true">✓</span>
          {{ $t('home.flight.flight_search.flight_saved') }}
        </div>
        <div class="flight-selector__selected-grid">
          <div class="flight-selector__result-main">
            <div class="flight-selector__flight-title">
              <strong>{{ flightHeading(selectedFlight) }}</strong>
              <airline-logo :airline="selectedFlight.airline" :size="24" />
            </div>
            <div class="flight-selector__route-row">
              <span>{{ flightRoute(selectedFlight) }}</span>
              <span v-if="selectedDomestic === true" class="flight-selector__domestic">
                {{ $t('home.flight.flight_search.domestic') }}
              </span>
            </div>
          </div>
          <div class="flight-selector__result-time">
            <strong>{{ formatRelevantDateTime(selectedFlight) }}</strong>
            <span>{{ airportSummary(selectedFlight) }}</span>
            <span v-if="relevantSegment(selectedFlight).terminal">
              {{ $t('home.flight.flight_search.terminal', { terminal: relevantSegment(selectedFlight).terminal }) }}
            </span>
          </div>
          <button type="button" class="flight-selector__outline-action" @click="chooseAnotherFlight">
            {{ $t('home.flight.flight_search.choose_another') }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flight-selector__tabs" role="tablist" :aria-label="$t('home.flight.flight_search.search_method')">
        <button
          type="button"
          role="tab"
          :aria-selected="mode === 'flight' ? 'true' : 'false'"
          :class="{ 'flight-selector__tab--active': mode === 'flight' }"
          class="flight-selector__tab"
          @click="setMode('flight')"
        >
          {{ $t('home.flight.flight_search.know_number') }}
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="mode === 'airport' ? 'true' : 'false'"
          :class="{ 'flight-selector__tab--active': mode === 'airport' }"
          class="flight-selector__tab"
          @click="setMode('airport')"
        >
          {{ $t('home.flight.flight_search.unknown_number') }}
        </button>
      </div>

      <p class="flight-selector__hint">
        {{ mode === 'flight' ? copy.numberHint : copy.airportHint }}
      </p>

      <p class="flight-selector__availability-note">
        <span class="flight-selector__availability-icon" aria-hidden="true">i</span>
        {{ $t('home.flight.flight_search.date_limit_note', { days: maxFutureDays }) }}
      </p>

      <form class="flight-selector__form" @submit.prevent="findFlights">
        <div
          class="flight-selector__fields"
          :class="`flight-selector__fields--${mode}`"
        >
          <app-form-group
            v-if="mode === 'flight'"
            :label="$t('home.flight.flight_search.flight_number')"
            label-for="flight-search-number"
            required
          >
            <app-input
              id="flight-search-number"
              v-model.trim="flightNumber"
              type="text"
              autocomplete="off"
              :aria-invalid="flightNumberInvalid ? 'true' : 'false'"
              :error="flightNumberInvalid"
              :disabled="selectionLoading"
            />
            <template #error>
              <div v-if="flightNumberInvalid">
                {{ $t('home.flight.flight_search.flight_number_error') }}
              </div>
            </template>
          </app-form-group>

          <app-form-group
            v-if="mode === 'airport'"
            :label="copy.airportLabel"
            required
          >
            <v-select
              v-model="airport"
              :reduce="item => item.code"
              :options="config.airports || []"
              :get-option-label="airportLabel"
              :filterable="false"
              :clearable="false"
              :searchable="false"
              :calculate-position="withPopper"
              :placeholder="copy.airportLabel"
              :disabled="selectionLoading"
              append-to-body
              class="select"
            >
              <template #open-indicator>
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
                  <path d="M7.872.418 7.61.138a.423.423 0 0 0-.619 0l-2.99 3.164L1.01.135a.422.422 0 0 0-.619 0L.128.413a.483.483 0 0 0 0 .655L3.691 4.85a.437.437 0 0 0 .31.149h.002a.437.437 0 0 0 .31-.149l3.56-3.773A.482.482 0 0 0 8 .747a.477.477 0 0 0-.128-.33Z" fill="#606060"/>
                </svg>
              </template>
              <template #option="option">
                <div class="select__item d-center">{{ airportLabel(option) }}</div>
              </template>
              <template #selected-option="option">
                <div class="selected d-center">{{ airportLabel(option) }}</div>
              </template>
            </v-select>
          </app-form-group>

          <app-form-group :label="copy.dateLabel" required>
            <date-picker
              v-model="searchDate"
              format="DD.MM.YYYY"
              value-type="YYYY-MM-DD"
              :placeholder="copy.dateLabel"
              :lang="calendarLanguage"
              :disabled-date="isDateDisabled"
              :disabled="selectionLoading"
              class="datepicker"
              :class="{ 'datepicker--error': dateInvalid }"
              popup-class="datepicker__popup"
              title-format="DD MMM YYYY"
            />
            <template #error>
              <div v-if="dateInvalid">
                {{ dateTooFar
                  ? $t('home.flight.flight_search.date_too_far', { days: maxFutureDays })
                  : $t('home.flight.travel_date_error')
                }}
              </div>
            </template>
          </app-form-group>

          <app-form-group
            v-if="mode === 'airport'"
            :label="copy.timeLabel"
            label-for="flight-search-time"
            required
          >
            <app-input
              id="flight-search-time"
              v-model="approximateTime"
              type="time"
              :aria-invalid="timeInvalid ? 'true' : 'false'"
              :error="timeInvalid"
              :disabled="selectionLoading"
            />
            <template #error>
              <div v-if="timeInvalid">
                {{ $t('validations.incorrect-time') }}
              </div>
            </template>
          </app-form-group>

          <app-button
            ref="findFlightButton"
            type="submit"
            class="flight-selector__find"
            :disabled="!canSearch"
            :aria-busy="searchLoading ? 'true' : 'false'"
          >
            {{ $t('home.flight.flight_search.find') }}
          </app-button>
        </div>
      </form>

      <div
        v-if="statusMessage"
        class="flight-selector__status"
        :class="{ 'flight-selector__status--error': status === 'error' }"
        role="status"
        aria-live="polite"
      >
        {{ statusMessage }}
      </div>

      <div v-if="results.length" class="flight-selector__results" aria-live="polite">
        <article
          v-for="result in results"
          :key="result.selection_token"
          class="flight-selector__result"
        >
          <div class="flight-selector__result-main">
            <div class="flight-selector__flight-title">
              <strong>{{ flightHeading(result) }}</strong>
              <airline-logo :airline="result.airline" :size="24" />
            </div>
            <div class="flight-selector__route-row">
              <span>{{ flightRoute(result) }}</span>
              <span v-if="result.domestic === true" class="flight-selector__domestic">
                {{ $t('home.flight.flight_search.domestic') }}
              </span>
            </div>
            <div v-if="hasFullRoute(result)" class="flight-selector__full-route">
              <span class="flight-selector__full-route-label">
                {{ $t('home.flight.flight_search.full_route') }}
              </span>
              <span class="flight-selector__route-airports">
                <span
                  v-for="(airport, index) in result.route"
                  :key="`${airport.airport_code || 'airport'}-${index}`"
                  class="flight-selector__route-stop"
                >
                  <span v-if="index" aria-hidden="true">→</span>
                  {{ airport.airport_code || '—' }}
                </span>
              </span>
            </div>
          </div>
          <div class="flight-selector__result-time">
            <strong>{{ formatRelevantDateTime(result) }}</strong>
            <span>{{ airportSummary(result) }}</span>
            <span v-if="relevantSegment(result).terminal">
              {{ $t('home.flight.flight_search.terminal', { terminal: relevantSegment(result).terminal }) }}
            </span>
          </div>
          <button
            type="button"
            class="flight-selector__use"
            :class="{ 'flight-selector__use--loading': selectingToken === result.selection_token }"
            :disabled="selectionLoading"
            :aria-busy="selectingToken === result.selection_token ? 'true' : 'false'"
            @click="useFlight(result)"
          >
            <img
              v-if="selectingToken === result.selection_token"
              src="@/assets/img/preloader.svg"
              alt=""
              class="flight-selector__button-preloader"
            />
            <template v-else>
              {{ $t('home.flight.flight_search.use_flight') }}
            </template>
          </button>
        </article>
      </div>
    </template>
  </section>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { getFlightOptions, selectFlight } from '@/http'
import AirlineLogo from '@/components/AirlineLogo'

const flightNumberPattern = /^(?=[a-zA-Z0-9]{0,1}[a-zA-Z])[a-zA-Z0-9]{2}[ -]?\d{1,4}[a-zA-Z]?$/
const timePattern = /^([01]\d|2[0-3]):[0-5]\d$/
const DEFAULT_MAX_FUTURE_DAYS = 180

export default {
  name: 'FlightSelector',
  components: {
    AirlineLogo,
  },
  props: {
    direction: {
      type: String,
      required: true,
      validator: value => ['arrival', 'departure'].includes(value),
    },
    config: {
      type: Object,
      required: true,
    },
    hash: {
      type: String,
      required: true,
    },
    lastName: {
      type: String,
      default: '',
    },
    initialDate: {
      type: String,
      default: '',
    },
    selectedFlight: {
      type: Object,
      default: null,
    },
    selectedDomestic: {
      type: Boolean,
      default: false,
    },
    dateDisabled: {
      type: Function,
      default: null,
    },
    resetKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      mode: 'flight',
      flightNumber: '',
      airport: '',
      searchDate: '',
      approximateTime: '',
      results: [],
      status: 'idle',
      statusMessage: '',
      searchLoading: false,
      selectionLoading: false,
      selectingToken: '',
      choosingAnother: false,
      requestGeneration: 0,
    }
  },
  computed: {
    copy() {
      const prefix = `home.flight.flight_search.${this.direction}`
      return {
        title: this.$t(`${prefix}.title`),
        description: this.$t(`${prefix}.description`),
        dateLabel: this.$t(`${prefix}.date`),
        airportLabel: this.$t(`${prefix}.airport`),
        timeLabel: this.$t(`${prefix}.time`),
        numberHint: this.$t(`${prefix}.number_hint`),
        airportHint: this.$t(`${prefix}.airport_hint`),
      }
    },
    calendarLanguage() {
      return localStorage.getItem('content_language') === 'ru' ? 'ru' : 'en'
    },
    showSelectedSummary() {
      return Boolean(this.selectedFlight) && !this.choosingAnother
    },
    normalizedFlightNumber() {
      return this.flightNumber.trim().toUpperCase()
    },
    flightNumberValid() {
      return flightNumberPattern.test(this.normalizedFlightNumber)
    },
    flightNumberInvalid() {
      return Boolean(this.flightNumber) && !this.flightNumberValid
    },
    timeValid() {
      return timePattern.test(this.approximateTime)
    },
    timeInvalid() {
      return Boolean(this.approximateTime) && !this.timeValid
    },
    dateInvalid() {
      return Boolean(this.searchDate) && !this.isSearchDateAllowed(this.searchDate)
    },
    dateTooFar() {
      const date = this.toIsoDate(this.searchDate)
      return Boolean(date) && date > this.maxSearchDateIso()
    },
    maxFutureDays() {
      const configuredDays = Number(this.config.max_future_days)
      if (Number.isInteger(configuredDays) && configuredDays > 0) return configuredDays
      return DEFAULT_MAX_FUTURE_DAYS
    },
    canSearch() {
      if (this.searchLoading || this.selectionLoading || !this.isSearchDateAllowed(this.searchDate)) return false
      if (this.mode === 'flight') return this.flightNumberValid
      return Boolean(this.airport) && this.timeValid
    },
    currentLocale() {
      const language = localStorage.getItem('content_language')
      if (language === 'ru') return 'ru-RU'
      if (language === 'de') return 'de-DE'
      return 'en-US'
    },
  },
  watch: {
    resetKey: {
      handler() {
        this.resetLocalState()
      },
      immediate: true,
    },
    selectedFlight() {
      this.choosingAnother = false
    },
    searchLoading(value) {
      this.$nextTick(() => {
        if (this.$refs.findFlightButton) this.$refs.findFlightButton.preload = value
      })
    },
  },
  beforeDestroy() {
    this.requestGeneration += 1
  },
  methods: {
    withPopper(dropdownList, component, { width }) {
      dropdownList.style.width = width
      const modal = component.$el.closest('.vm--modal')
      const boundary = modal || 'clippingParents'
      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'flip',
            options: {
              boundary,
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              boundary,
              padding: 8,
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
          {
            name: 'limitToModal',
            enabled: Boolean(modal),
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn({ state }) {
              const modalRect = modal.getBoundingClientRect()
              const toggleRect = component.$refs.toggle.getBoundingClientRect()
              let availableHeight = modalRect.bottom - toggleRect.bottom
              if (state.placement.startsWith('top')) availableHeight = toggleRect.top - modalRect.top
              state.styles.popper.maxHeight = `${Math.max(0, Math.min(174, availableHeight - 8))}px`
            },
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({ state }) {
              component.$el.classList.toggle('drop-up', state.placement.startsWith('top'))
            },
          },
        ],
      })
      return () => popper.destroy()
    },
    resetLocalState() {
      this.requestGeneration += 1
      this.mode = 'flight'
      this.searchDate = this.initialDate || ''
      this.flightNumber = ''
      this.airport = ''
      this.approximateTime = ''
      this.results = []
      this.status = 'idle'
      this.statusMessage = ''
      this.searchLoading = false
      this.selectionLoading = false
      this.selectingToken = ''
      this.choosingAnother = false
    },
    setMode(mode) {
      if (this.mode === mode || this.selectionLoading) return
      this.requestGeneration += 1
      this.mode = mode
      this.results = []
      this.status = 'idle'
      this.statusMessage = ''
      this.searchLoading = false
    },
    chooseAnotherFlight() {
      this.choosingAnother = true
      this.results = []
      this.status = 'idle'
      this.statusMessage = ''
    },
    toIsoDate(value) {
      if (!value) return ''
      if (value instanceof Date) {
        if (isNaN(value.getTime())) return ''
        const year = value.getFullYear()
        const month = String(value.getMonth() + 1).padStart(2, '0')
        const day = String(value.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return ''
      const [year, month, day] = value.split('-').map(Number)
      const date = new Date(Date.UTC(year, month - 1, day))
      if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return ''
      return value
    },
    todayIso() {
      return this.toIsoDate(new Date())
    },
    maxSearchDateIso() {
      const date = new Date()
      date.setDate(date.getDate() + this.maxFutureDays)
      return this.toIsoDate(date)
    },
    isSearchDateAllowed(value) {
      const date = this.toIsoDate(value)
      if (!date || date < this.todayIso() || date > this.maxSearchDateIso()) return false
      return !this.dateDisabled || !this.dateDisabled(date)
    },
    isDateDisabled(value) {
      return !this.isSearchDateAllowed(value)
    },
    buildSearchParams() {
      const params = {
        direction: this.direction,
        mode: this.mode,
        date: this.searchDate,
      }
      if (this.mode === 'flight') {
        params.flight = this.normalizedFlightNumber
      } else {
        params.airport = this.airport
        params.hour = Number(this.approximateTime.slice(0, 2))
      }
      return params
    },
    serializeParams(params) {
      return JSON.stringify(params)
    },
    async findFlights() {
      if (!this.canSearch) return
      const params = this.buildSearchParams()
      const snapshot = this.serializeParams(params)
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.searchLoading = true
      this.results = []
      this.status = 'loading'
      this.statusMessage = ''
      try {
        const response = await getFlightOptions(this.hash, params, this.lastName)
        if (generation !== this.requestGeneration || snapshot !== this.serializeParams(this.buildSearchParams())) return
        const results = Array.isArray(response.data?.results) ? response.data.results : []
        this.results = results
        if (results.length) {
          this.status = 'results'
        } else {
          this.status = 'empty'
          this.statusMessage = this.$t('home.flight.flight_search.empty')
        }
      } catch (error) {
        if (generation !== this.requestGeneration) return
        this.status = 'error'
        this.statusMessage = this.getRequestError(error, 'search')
      } finally {
        if (generation === this.requestGeneration) this.searchLoading = false
      }
    },
    async useFlight(result) {
      if (this.selectionLoading || !result.selection_token) return
      const generation = this.requestGeneration + 1
      this.requestGeneration = generation
      this.selectionLoading = true
      this.selectingToken = result.selection_token
      this.status = 'loading'
      this.statusMessage = ''
      try {
        const response = await selectFlight(this.hash, {
          direction: this.direction,
          selection_token: result.selection_token,
        }, this.lastName)
        if (generation !== this.requestGeneration) return
        this.$emit('selected', response.data)
      } catch (error) {
        if (generation !== this.requestGeneration) return
        const message = this.getRequestError(error, 'selection')
        const lowerMessage = message.toLowerCase()
        if (error.response?.status === 400 && (lowerMessage.includes('expired') || lowerMessage.includes('invalid'))) {
          this.results = []
        }
        this.status = 'error'
        this.statusMessage = message
      } finally {
        if (generation === this.requestGeneration) {
          this.selectionLoading = false
          this.selectingToken = ''
        }
      }
    },
    getRequestError(error, requestType) {
      const status = error.response?.status
      const data = error.response?.data
      if (data?.code === 'flight_search_date_too_far') {
        const days = Number(data.max_future_days) || this.maxFutureDays
        return this.$t('home.flight.flight_search.date_too_far', { days })
      }
      if (status === 429) return this.$t('home.flight.flight_search.rate_limit')
      if (status === 503) return this.$t('home.flight.flight_search.unavailable')
      if (typeof data?.detail === 'string' && data.detail) return data.detail
      const messages = []
      if (data && typeof data === 'object') {
        Object.keys(data).forEach(key => {
          if (key === 'detail') return
          const value = data[key]
          if (Array.isArray(value)) {
            value.forEach(item => {
              if (typeof item === 'string') messages.push(item)
            })
          } else if (typeof value === 'string') {
            messages.push(value)
          }
        })
      }
      if (messages.length) return messages.join(' ')
      return this.$t(`home.flight.flight_search.${requestType}_error`)
    },
    airportLabel(airport) {
      if (!airport) return ''
      return airport.code ? `${airport.code} · ${airport.name}` : airport.name
    },
    flightHeading(flight) {
      const airline = flight.airline?.name || flight.airline?.iata || this.$t('home.flight.flight_search.airline_unknown')
      return `${airline} · ${flight.flight_number || ''}`
    },
    flightRoute(flight) {
      const departure = flight.departure?.airport_code || '—'
      const arrival = flight.arrival?.airport_code || '—'
      return `${departure} → ${arrival}`
    },
    hasFullRoute(flight) {
      return Array.isArray(flight.route) && flight.route.length > 2
    },
    relevantSegment(flight) {
      return flight[this.direction] || {}
    },
    relevantAirportName(flight) {
      const segment = this.relevantSegment(flight)
      const airportCode = segment.airport_code
      const configuredAirport = (this.config.airports || []).find(airport => airport.code === airportCode)
      return configuredAirport?.name || segment.airport_name || airportCode || '—'
    },
    airportSummary(flight) {
      return this.$t(`home.flight.flight_search.${this.direction}.airport_summary`, {
        airport: this.relevantAirportName(flight),
      })
    },
    formatRelevantDateTime(flight) {
      return this.formatSegmentDateTime(this.relevantSegment(flight))
    },
    formatSegmentDateTime(segment) {
      if (!segment?.scheduled_at) return '—'
      if (!segment.timezone) return segment.scheduled_at.slice(0, 16).replace('T', ' ')
      try {
        return new Intl.DateTimeFormat(this.currentLocale, {
          timeZone: segment.timezone,
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).format(new Date(segment.scheduled_at))
      } catch (error) {
        return segment.scheduled_at.slice(0, 16).replace('T', ' ')
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.flight-selector
  margin: 28px 0 0
  padding: 20px
  border: 1px solid #cfe4f1
  border-radius: 12px
  background: #f5fbff
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04)
  color: $color-text-base

.flight-selector__header
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 20px
  margin-bottom: 20px

.flight-selector__intro
  display: flex
  align-items: center
  gap: 12px
  min-width: 0

.flight-selector__badge
  display: flex
  flex: 0 0 40px
  align-items: center
  justify-content: center
  width: 40px
  height: 40px
  border-radius: 10px
  background: #439ad1
  box-shadow: 1px 3px 8px rgba(0, 0, 0, 0.12)

  svg
    width: 22px
    height: 22px
    fill: #fff

.flight-selector__title
  margin: 0 0 4px
  color: $color-text-base
  font-size: 16px
  font-weight: 600
  line-height: 19px

.flight-selector__description,
.flight-selector__hint
  margin: 0
  color: $color-text-second
  font-size: 13px
  line-height: 16px

.flight-selector__text-action,
.flight-selector__outline-action
  color: #247bb4
  font-size: 13px
  font-weight: 500

  &:focus-visible
    outline: 2px solid #247bb4
    outline-offset: 3px

.flight-selector__text-action
  flex: 0 0 auto
  padding: 9px 14px
  border: 1px solid #b9d6e8
  border-radius: 8px
  background: #eaf5fb

  &:hover
    border-color: #8bbbd8
    background: #dff0f9

.flight-selector__tabs
  display: inline-flex
  max-width: 100%
  margin-bottom: 10px
  padding: 3px
  border: 1px solid #cadfeb
  border-radius: 8px
  background: #eaf4fa

.flight-selector__tab
  min-height: 30px
  padding: 7px 14px
  border-radius: 6px
  color: #587187
  font-size: 13px
  font-weight: 500

  &:focus-visible
    outline: 2px solid #247bb4
    outline-offset: 1px

.flight-selector__tab--active
  background: #fff
  color: #247bb4
  box-shadow: 0 2px 7px rgba(39, 91, 123, 0.12)

.flight-selector__hint
  margin-bottom: 10px

.flight-selector__availability-note
  display: flex
  align-items: flex-start
  gap: 8px
  margin: 0 0 16px
  padding: 9px 11px
  border: 1px solid #cfe2ed
  border-radius: 8px
  background: #f2f8fb
  color: #587187
  font-size: 12px
  line-height: 16px

.flight-selector__availability-icon
  display: inline-flex
  flex: 0 0 16px
  align-items: center
  justify-content: center
  width: 16px
  height: 16px
  border-radius: 50%
  background: #439ad1
  color: #fff
  font-size: 11px
  font-style: normal
  font-weight: 600

.flight-selector__fields
  display: grid
  align-items: start
  gap: 20px

  ::v-deep .form-group
    margin-bottom: 0

.flight-selector__fields--flight
  grid-template-columns: minmax(210px, 1fr) minmax(185px, .65fr) auto

.flight-selector__fields--airport
  grid-template-columns: minmax(190px, 1fr) minmax(175px, .8fr) minmax(165px, .7fr) auto

.flight-selector__find
  min-width: 130px
  min-height: 38px
  padding: 12px 14px 11px
  border-radius: 8px
  background: #439ad1
  box-shadow: 1px 3px 8px rgba(0, 0, 0, 0.15)
  color: #fff
  font-size: 15px
  font-weight: 500
  margin-top: 25px

  &.btn--default:hover:not(:disabled)
    background: #2e87bf

  &:focus-visible
    outline: 2px solid #247bb4
    outline-offset: 3px

  &.btn--beauty
    background: #c4c4c4

  &.btn--beauty.btn--preload
    background: #439ad1

.flight-selector__status
  margin-top: 16px
  padding: 10px 12px
  border: 1px solid $color-border
  border-radius: 6px
  background: #fff
  color: $color-text-second
  font-size: 13px
  line-height: 16px

.flight-selector__status--error
  border-color: $color-error
  background: #fff8f8
  color: $color-error

.flight-selector__results
  display: grid
  gap: 10px
  margin-top: 16px

.flight-selector__result
  display: grid
  grid-template-columns: minmax(180px, 1.2fr) minmax(170px, .8fr) auto
  align-items: center
  gap: 20px
  padding: 12px 14px
  border: 1px solid $color-border
  border-radius: 10px
  background: #fff

.flight-selector__result-main,
.flight-selector__result-time
  display: flex
  flex-direction: column
  gap: 5px
  min-width: 0

  strong
    overflow: hidden
    color: $color-text-base
    font-size: 13px
    font-weight: 600
    text-overflow: ellipsis
    white-space: nowrap

  span
    color: $color-text-second
    font-size: 13px

.flight-selector__flight-title
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 6px 10px
  min-width: 0

  strong
    flex: 0 1 auto
    max-width: 100%

.flight-selector__route-row
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 6px

.flight-selector__domestic
  display: inline-flex
  align-items: center
  padding: 2px 6px
  border-radius: 4px
  background: #edf7e5
  color: #4f861d!important
  font-size: 10px!important
  font-weight: 600
  line-height: 13px
  letter-spacing: .04em
  text-transform: uppercase

.flight-selector__full-route
  display: flex
  align-items: baseline
  flex-wrap: wrap
  gap: 4px 8px
  min-width: 0

.flight-selector__full-route-label
  color: #587187!important
  font-size: 12px!important
  font-weight: 500

.flight-selector__route-airports
  display: inline-flex
  flex-wrap: wrap
  gap: 3px
  min-width: 0

.flight-selector__route-stop
  display: inline-flex
  gap: 3px
  color: #247bb4!important
  font-size: 12px!important
  font-weight: 500
  white-space: nowrap

.flight-selector__use,
.flight-selector__outline-action
  display: inline-flex
  align-items: center
  justify-content: center
  min-height: 38px
  padding: 10px 12px 9px
  border: 1px solid #439ad1
  border-radius: 8px
  background: #fff
  color: #247bb4
  font-size: 13px
  font-weight: 500

  &:hover:not(:disabled)
    background: #eef8fd

  &:focus-visible
    outline: 2px solid #247bb4
    outline-offset: 2px

  &:disabled
    opacity: .55
    cursor: not-allowed

.flight-selector__use--loading:disabled
  border-color: #439ad1
  background: #439ad1
  opacity: 1

.flight-selector__button-preloader
  width: 20px
  height: 20px

.flight-selector__selected
  padding: 14px 16px
  border: 1px solid #91c668
  border-radius: 10px
  background: #fff

.flight-selector__selected-label
  display: flex
  align-items: center
  gap: 9px
  margin-bottom: 14px
  color: #4f861d
  font-size: 13px
  font-weight: 500
  letter-spacing: .03em
  text-transform: uppercase

.flight-selector__check
  display: inline-flex
  align-items: center
  justify-content: center
  width: 22px
  height: 22px
  border-radius: 50%
  background: #67ad1d
  color: #fff

.flight-selector__selected-grid
  display: grid
  grid-template-columns: minmax(180px, 1.2fr) minmax(180px, .85fr) auto
  align-items: center
  gap: 24px

@media (max-width: 850px)
  .flight-selector
    margin-top: 20px
    padding: 16px

  .flight-selector__fields--flight,
  .flight-selector__fields--airport
    grid-template-columns: 1fr 1fr

  .flight-selector__find
    margin-top: 0
    grid-column: 2
    width: 100%

  .flight-selector__result
    grid-template-columns: 1fr 1fr

  .flight-selector__selected-grid
    grid-template-columns: 1fr 1fr

  .flight-selector__use,
  .flight-selector__outline-action
    grid-column: 1 / -1
    width: 100%

@media (max-width: 479px)
  .flight-selector
    padding: 14px

  .flight-selector__header
    align-items: stretch
    flex-direction: column
    gap: 12px

  .flight-selector__text-action
    align-self: flex-start

  .flight-selector__tabs
    display: flex
    width: 100%

  .flight-selector__tab
    flex: 1
    padding: 8px 9px
    font-size: 12px

  .flight-selector__fields--flight,
  .flight-selector__fields--airport,
  .flight-selector__result,
  .flight-selector__selected-grid
    grid-template-columns: 1fr

  .flight-selector__find
    grid-column: 1

  .flight-selector__outline-action
    width: 100%
</style>
